import classNames from 'classnames';
import { Badge, Button } from 'flowbite-react';
import React from 'react';
import { BiChevronRight } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import { useThemeContext } from 'src/context/ThemeProvider';
import { PracticeQuestion, PrepPlanBadgeStatus } from 'types';

interface Props {
	question: PracticeQuestion;
	status: PrepPlanBadgeStatus;
}

export const convertDifficulty = (difficulty?: number) => {
	if (difficulty !== undefined) {
		if (difficulty <= 0.33) {
			return 'Easy';
		}
		if (difficulty > 0.33 && difficulty < 0.66) {
			return 'Medium';
		}
		if (difficulty >= 0.66) {
			return 'Difficult';
		}
	}
};

const PrepareQuestionCard = ({ question, status }: Props) => {
	const navigate = useNavigate();
	const { themeMode } = useThemeContext();
	const statusImage = {
		[PrepPlanBadgeStatus.LOCKED]: '/assets/prepPlanLocked.svg',
		[PrepPlanBadgeStatus.IN_PROGRESS]:
			themeMode === 'dark' ? '/assets/prepPlanProgress.svg' : '/assets/prepPlanProgressLight.svg',
		[PrepPlanBadgeStatus.COMPLETED]: '/assets/prepPlanCompleted.svg',
	};
	return (
		<button
			onClick={() => {
				if ([PrepPlanBadgeStatus.IN_PROGRESS, PrepPlanBadgeStatus.COMPLETED].includes(status)) {
					// window.open(`/prepare/${question._id}/attempt/${question.assessQuestionId}`, '_blank', 'noopener,noreferrer');
					navigate(`/prepare/${question._id}/attempt/${question.assessQuestionId}`);
				}
			}}
			style={{ boxShadow: question?.isAttempted ? '0px 4px 4px 0px rgba(255, 255, 255, 0.07)' : 'none' }}
			className={classNames(
				`${
					[PrepPlanBadgeStatus.IN_PROGRESS, PrepPlanBadgeStatus.COMPLETED].includes(status) ? 'cursor-pointer' : ''
				} text-left border rounded-[12px]`,
				question?.isAttempted ? 'border-[#6698FA]' : 'border-[#EBEBF0] dark:border-[#555770]',
			)}
		>
			<div className="dark:bg-[#1F2A37] bg-[#FFF] px-[16px] py-[12px] rounded-tl-[12px] rounded-tr-[12px]">
				<div className="flex items-start gap-[12px]">
					<img
						src={question.isAttempted ? statusImage[PrepPlanBadgeStatus.COMPLETED] : statusImage[status]}
						alt={question.title}
						className="w-[32px] h-[32px]"
					/>
					<p className="text-[18px] font-[500] leading-[28px] dark:text-[#fff] text-[#1F3343] font-inter mt-[1px]">
						{question.title}
					</p>
				</div>

				<div className="items-center gap-[5px] flex md:hidden mt-3">
					<Badge
						className={
							'dark:!bg-[#374151] bg-[F3F4F6] w-fit dark:!text-[#E3FFF1] text-[#111928] py-[2px] px-[10px] rounded-[6px] border-[#06C270] border-[0.5px]'
						}
					>
						{convertDifficulty(question.difficulty)}
					</Badge>
					{question.skillNames && question.skillNames?.length > 0 && (
						<>
							<div>
								<Badge
									className={
										'dark:!bg-[#374151] bg-[#F3F4F6] w-fit dark:!text-[#E3FFF1] text-[#111928] py-[2px] px-[10px] rounded-[6px] border-[#0063F7] border-[0.5px]'
									}
								>
									{question.skillNames[0]}
								</Badge>
							</div>
						</>
					)}
				</div>
			</div>
			<div className="min-h-[46px] flex items-center justify-between dark:bg-[#18222D] bg-[#FAFAFC] px-4 md:px-[20px] py-[10px] rounded-bl-[12px] rounded-br-[12px] ">
				<div className="flex items-center md:gap-4">
					<Badge
						className={
							'dark:!bg-[#374151] bg-[F3F4F6] w-fit dark:!text-[#E3FFF1] text-[#111928] py-[2px] px-[10px] rounded-[6px] border-[#06C270] border-[0.5px] hidden md:block'
						}
					>
						{convertDifficulty(question.difficulty)}
					</Badge>
					{question.skillNames && question.skillNames?.length > 0 && (
						<>
							<div className="w-[4px] h-[4px] bg-[#C7C9D9] dark:bg-[#F2F2F5] rounded-full hidden md:block"></div>
							<div>
								<Badge
									className={
										'dark:!bg-[#374151] bg-[#F3F4F6] w-fit dark:!text-[#E3FFF1] text-[#111928] py-[2px] px-[10px] rounded-[6px] border-[#0063F7] border-[0.5px] hidden md:block'
									}
								>
									{question.skillNames[0]}
								</Badge>
							</div>
						</>
					)}
					{question.totalUsersAttempted && (
						<>
							<div className="w-[4px] h-[4px] bg-[#C7C9D9] dark:bg-[#F2F2F5] rounded-full hidden md:block"></div>
							<div>
								<p className="flex items-center gap-[4px] text-[14px] font-[400] leading-[20px] dark:text-[#C7C9D9] text-[#8F90A6] font-inter">
									<img src="/assets/check_green.svg" alt="total users" className="dark:block hidden" />{' '}
									<img src="/assets/check_greenLight.svg" alt="total users" className="dark:hidden" />{' '}
									{question.totalUsersAttempted} {question.totalUsersAttempted === 1 ? `user` : `users`} Completed
								</p>
							</div>
						</>
					)}
				</div>

				<div className="hidden md:block">
					<BiChevronRight
						size={'24px'}
						className={classNames(
							[PrepPlanBadgeStatus.IN_PROGRESS, PrepPlanBadgeStatus.COMPLETED].includes(status)
								? 'text-[#6698FA]'
								: 'dark:text-gray-600 text-[#7777E7]',
						)}
					/>
				</div>

				<div className="md:hidden">
					<Button
						size="sm"
						color="purplePlaced"
						className="max-h-[30px]"
						disabled={![PrepPlanBadgeStatus.IN_PROGRESS, PrepPlanBadgeStatus.COMPLETED].includes(status)}
					>
						Attempt
					</Button>
				</div>
			</div>
		</button>
	);
};

export default PrepareQuestionCard;
